import React, { useContext } from "react"
import { observer } from "mobx-react"
import { Anchor, Button, Paper, Tabs, Text, Title } from "@mantine/core"
import { IconArrowRight, IconCode, IconSettings } from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import type { Framework } from "../../../docs/types"
import type { LandingNoCodeSetup } from "../../../docs/landing"
import { NWC_PATH, SWAP_PROVIDER_PATH, WORDPRESS_PATH } from "../../helpers/constants"

// The two ways into a store plugin that needs no code (WooCommerce): its own
// admin screens, or a coding agent. The admin tab is the default because most
// store owners never open a coding agent. Each tab has a hash of its own, so
// a video description or a support reply can link straight to one.
export const WP_ADMIN_HASH = "wp-admin"
export const AGENT_HASH = "agent"

// **bold** and `code`, the only marks the step text uses; FrameworkPage.inline
// draws the same two for the server body.
export const inline = (text: string): React.ReactNode[] =>
  text.split(/(\*\*.+?\*\*|`.+?`)/).map((part, index) => {
    if (part.length > 4 && part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    if (part.length > 2 && part.startsWith("`") && part.endsWith("`")) {
      return <code key={index}>{part.slice(1, -1)}</code>
    }
    return part
  })

type NoCodeSetupProps = {
  framework: Framework
  setup: LandingNoCodeSetup
  // The agent tab's body: the shared copy buttons and their caption.
  agentActions: React.ReactNode
  // Under both tabs: the quickstart and plugin source buttons.
  footer: React.ReactNode
}

export const NoCodeSetup: React.FC<NoCodeSetupProps> = observer(
  ({ framework, setup, agentActions, footer }) => {
    const workspace = useContext(AppWorkspaceContext)
    const tab = workspace.hash === AGENT_HASH ? AGENT_HASH : WP_ADMIN_HASH

    return (
      <Paper
        className="or-integration-setup or-nocode-setup"
        radius="lg"
        p={{ base: "md", sm: "xl" }}
        withBorder
      >
        <Text className="or-eyebrow">Set up</Text>
        <Title order={2} className="or-quicksetup-title">
          {setup.title}
        </Title>
        <Text className="or-quicksetup-lede">{setup.lede}</Text>
        {framework.requires ? (
          <Text className="or-integration-requires">
            Requires {framework.requires}.{" "}
            <Anchor
              href={setup.requiresHref}
              className="or-nowrap"
              onClick={(event) => {
                event.preventDefault()
                workspace.openHref(setup.requiresHref)
              }}
            >
              {setup.requiresLabel} <IconArrowRight size={14} aria-hidden="true" />
            </Anchor>
          </Text>
        ) : null}

        {/* The id follows the open tab, so #wp-admin and #agent both scroll
            here and the hash is also the tab. */}
        <Tabs
          id={tab}
          value={tab}
          onChange={(value) => workspace.setHash(value ?? WP_ADMIN_HASH, true)}
          variant="pills"
          color="orGreen"
          keepMounted={false}
          className="or-nocode-tabs"
          mt="lg"
        >
          <Tabs.List mb="lg">
            <Tabs.Tab value={WP_ADMIN_HASH} leftSection={<IconSettings size={16} />}>
              {setup.tab}
            </Tabs.Tab>
            <Tabs.Tab value={AGENT_HASH} leftSection={<IconCode size={16} />}>
              {setup.agentTab}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={WP_ADMIN_HASH}>
            <Text className="or-nocode-before-title">{setup.beforeTitle}</Text>
            <div className="or-nocode-before">
              <div className="or-nocode-before-item">
                <Text className="or-quicksetup-step-title">A receive-only NWC code</Text>
                <Text className="or-quicksetup-step-body">{setup.nwc}</Text>
                <Button component="a" href={NWC_PATH} size="sm" mt="sm">
                  Get a receive-only NWC code
                </Button>
              </div>
              <div className="or-nocode-before-item">
                <Text className="or-quicksetup-step-title">
                  A Lightning Swap Connect code{" "}
                  <Text span className="or-quicksetup-optional">
                    (optional)
                  </Text>
                </Text>
                <Text className="or-quicksetup-step-body">{setup.lsc}</Text>
                <Button component="a" href={SWAP_PROVIDER_PATH} size="sm" variant="default" mt="sm">
                  Set up a swap provider
                </Button>
              </div>
            </div>

            <ol className="or-nocode-steps">
              {setup.steps.map((step, index) => {
                const src = workspace.landingImageUrl(step.image)
                return (
                  <li key={step.image} className="or-quicksetup-step or-quicksetup-step-wide">
                    <Text className="or-quicksetup-step-title">
                      {index + 1}. {step.title}
                    </Text>
                    <Text className="or-quicksetup-step-body">{inline(step.body)}</Text>
                    {src ? (
                      <a href={src} target="_blank" rel="noopener noreferrer" className="or-nocode-shot-link">
                        <img
                          className="or-nocode-shot"
                          src={src}
                          alt={step.alt}
                          width={step.width}
                          height={step.height}
                          loading="lazy"
                        />
                      </a>
                    ) : null}
                  </li>
                )
              })}
            </ol>
            <Text className="or-nocode-cli" size="sm" c="dimmed">
              {inline(setup.cli.replace("{install}", framework.install))}
            </Text>
          </Tabs.Panel>

          <Tabs.Panel value={AGENT_HASH}>
            <Text className="or-quicksetup-lede" mt={0} mb="md">
              {setup.agentLede}
            </Text>
            {agentActions}
          </Tabs.Panel>
        </Tabs>

        {footer}
      </Paper>
    )
  }
)

// The demo section of a no-code page: the order-pay page with its payment
// method grid, and the plugin README one link away. The README's build and
// WP-CLI sections are for developers and stay on its own page.
export const NoCodeCheckout: React.FC<{ setup: LandingNoCodeSetup }> = observer(({ setup }) => {
  const workspace = useContext(AppWorkspaceContext)
  const image = setup.checkoutImage
  const src = workspace.landingImageUrl(image.image)
  return (
    <>
      <Title order={2} className="or-demo-title">
        {setup.checkoutTitle}
      </Title>
      <Text className="or-demo-lede">{setup.checkoutLede}</Text>
      {src ? (
        <img
          className="or-nocode-checkout"
          src={src}
          alt={image.alt}
          width={image.width}
          height={image.height}
          loading="lazy"
        />
      ) : null}
      {/* The README's own path, not the guide: the link needs no markdown, so
          this page does not load every guide's to draw it. */}
      <Text size="sm" mt="md">
        <Anchor
          href={WORDPRESS_PATH}
          onClick={(event) => {
            event.preventDefault()
            workspace.openHref(WORDPRESS_PATH)
          }}
        >
          {setup.readmeLabel} <IconArrowRight size={14} aria-hidden="true" />
        </Anchor>
      </Text>
    </>
  )
})
